import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApolloError, useMutation } from "@apollo/client";
import { TFunction } from "i18next";
import { MY_TIMESHEET_GET_GROUPED_BY_USERS_DASHBOARD_WEEKLY_LIST } from "my-timesheet/graphql";
import { sumTimeStrings } from "my-timesheet/utils";
import { RoleFilterInput } from "oc/types";
import parsePipedName from "oc/utils/get-parse-pipedName";

// --- Types and Interfaces ---
interface Day {
  duration: string;
}

interface WeekItem {
  weekIndex: number;
}

interface ColumnDef {
  id: string;
  label: string;
  align: "left" | "center";
  width: string;
  type: "avatar" | "text" | "actionArrow";
  sort?: boolean;
  search?: boolean;
}

interface Row {
  contractId: string;
  employeeName: string;
  employeeId: string;
  days: Day[];
}

interface DataType {
  page: number;
  pageSize: number;
  rows: Row[];
  total: number;
  totalPages: number;
}

interface ParsedRow {
  _id: string;
  employeeName: string;
  [dayIndex: number]: string;
  sumDuration: string;
}

interface TableData {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: string;
  searchField?: string;
  searchText?: string;
}

interface UseMySheetProps {
  periodStart?: string;
  periodEnd?: string;
  period: "week" | string;
  tableData?: TableData;
  roleFilters?: RoleFilterInput;
  role?: string;
}

// Generated by GraphQL codegen or define manually:
type GetGroupedByUsersDashboardWeeklyListData = {
  groupedByUsersDashboardWeeklyList: DataType;
};

type GetGroupedByUsersDashboardWeeklyListVars = {
  page?: number;
  pageSize?: number;
  startTime?: string;
  endTime?: string;
  roleFilters?: RoleFilterInput;
  sortField?: string;
  sortDirection?: string;
  searchField?: string;
  searchText?: string;
  role?: string;
};

// --- Helper Functions ---
const generateColumnDefs = (
  items: Day[] | WeekItem[],
  t: TFunction,
  type: "week" = "week",
): ColumnDef[] => {
  const baseItems: ColumnDef[] = [
    {
      id: "avatar",
      label: t("OC_IMAGE"),
      align: "center",
      width: "85px",
      type: "avatar",
    },
    {
      id: "employeeName",
      label: t("OC_NAME"),
      align: "left",
      width: "auto",
      type: "text",
      sort: true,
      search: true,
    },
  ];

  const dynamicItems: ColumnDef[] = (items as any[]).map((item, index) => ({
    id: `${type === "week" ? index : (item as WeekItem).weekIndex - 1}`,
    label:
      type === "week"
        ? t(
            [
              "MY_TIMESHEET_MONDAY",
              "MY_TIMESHEET_TUESDAY",
              "MY_TIMESHEET_WEDNESDAY",
              "MY_TIMESHEET_THURSDAY",
              "MY_TIMESHEET_FRIDAY",
              "MY_TIMESHEET_SATURDAY",
              "MY_TIMESHEET_SUNDAY",
            ][index],
          )
        : `${(item as WeekItem).weekIndex}. ${t("MY_TIMESHEET_WEEK")}`,
    align: "left",
    width: "100px",
    type: "text",
  }));

  const summaryItem: ColumnDef = {
    id: "sumDuration",
    label: t("OC_SUMMARY"),
    align: "left",
    type: "text",
    width: "100px",
    sort: true,
  };

  return [...baseItems, ...dynamicItems, summaryItem];
};

const generateColumnDefsIsMobile = (
  weeks: Day[],
  t: TFunction,
): ColumnDef[] => {
  const baseItems: ColumnDef[] = [
    {
      id: "avatar",
      label: t("OC_IMAGE"),
      align: "center",
      width: "85px",
      type: "avatar",
    },
    {
      id: "employeeName",
      label: t("OC_NAME"),
      align: "left",
      width: "auto",
      type: "text",
      sort: true,
      search: true,
    },
    {
      id: "actionArrow",
      type: "actionArrow",
      label: "",
      align: "center",
      width: "40px",
    },
  ];

  return [...baseItems];
};

// --- Main Hook ---
export const useMyTimesheetWorktimeSummaryWeeklyData = ({
  periodStart,
  periodEnd,
  period,
  tableData,
  roleFilters,
  role,
}: UseMySheetProps) => {
  const { t, i18n } = useTranslation();
  const [queryData, setQueryData] = useState<DataType | undefined>();

  const [runQuery, { loading, error }] = useMutation<
    GetGroupedByUsersDashboardWeeklyListData,
    GetGroupedByUsersDashboardWeeklyListVars
  >(MY_TIMESHEET_GET_GROUPED_BY_USERS_DASHBOARD_WEEKLY_LIST, {
    variables: {
      page: tableData?.page,
      pageSize: tableData?.pageSize,
      startTime: periodStart,
      endTime: periodEnd,
      roleFilters: roleFilters as RoleFilterInput,
      sortField: tableData?.sortField,
      sortDirection: tableData?.sortDirection,
      searchField: tableData?.searchField,
      searchText: tableData?.searchText,
      role,
    },
    onCompleted(data) {
      setQueryData(data.groupedByUsersDashboardWeeklyList);
    },
    onError(err: ApolloError) {
      console.error("Mutation error:", err);
    },
  });

  if (!periodStart || !periodEnd || period !== "week") {
    return {
      columnDefsMobile: { id: "", columns: [] },
      columnDefsDesktop: { id: "", columns: [] },
      data: [],
      loading: false,
      error: null,
      total: 0,
      totalPages: 0,
      page: 0,
      pageSize: 0,
      runQuery: () => {},
      queryData: undefined,
    };
  }

  const data: DataType = queryData || {
    page: 1,
    pageSize: 10,
    rows: [],
    total: 0,
    totalPages: 0,
  };

  const rows: Row[] = data.rows || [];
  const days: Day[] = rows[0]?.days || [];

  const columnDefsMobile = {
    id: "my-time-sheet-my-requests-dashboard-table-mobile",
    columns: generateColumnDefsIsMobile(days, t),
  };

  const columnDefsDesktop = {
    id: "my-time-sheet-my-requests-dashboard-table-desktop",
    columns: generateColumnDefs(days, t),
  };

  const parsedRows: ParsedRow[] = rows.map((item) => {
    const newItem: any = { ...item };
    newItem.employeeName = parsePipedName(item.employeeName, i18n);
    delete newItem.days;
    delete newItem.employeeId;

    let sumDuration = "00:00";
    const dayDurations = (item.days || []).map((day) => {
      sumDuration = sumTimeStrings([sumDuration, day.duration]);
      return day.duration;
    });

    const result: ParsedRow = {
      ...newItem,
      ...dayDurations,
      sumDuration,
      _id: item.contractId,
    } as ParsedRow;

    return result;
  });

  return {
    columnDefsMobile,
    columnDefsDesktop,
    data: parsedRows,
    loading,
    error,
    total: data.total,
    totalPages: data.totalPages,
    page: data.page,
    pageSize: data.pageSize,
    runQuery,
    queryData: data,
  };
};

export default useMyTimesheetWorktimeSummaryWeeklyData;
